import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import { getLocalizedValue, SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialCategory = (supportedLanguages, category) => ({
  id: category?.id ?? null,
  name: buildLocalizedFields(supportedLanguages, category?.name),
  description: buildLocalizedFields(supportedLanguages, category?.description),
  isActive: category?.isActive !== false,
  serviceIds: Array.isArray(category?.serviceIds) ? [...category.serviceIds] : [],
});

const buildTextFieldSx = (hasError = false, multiline = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  lineHeight: multiline ? 1.5 : 'normal',
  resize: multiline ? 'vertical' : 'none',
  minHeight: multiline ? 96 : 48,
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const ServiceCategoryEditorDialog = ({
  open,
  mode,
  category,
  supportedLanguages,
  services,
  saving,
  onClose,
  onSave,
}) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState(() => buildInitialCategory(supportedLanguages, category));
  const [errors, setErrors] = useState({});
  const sortedLanguages = useMemo(
    () => [...supportedLanguages].sort(
      (a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b)
    ),
    [supportedLanguages]
  );
  const [activeLanguage, setActiveLanguage] = useState(() => supportedLanguages[0] || 'en');

  useEffect(() => {
    if (open) {
      setFormData(buildInitialCategory(supportedLanguages, category));
      setErrors({});
    }
  }, [open, category, supportedLanguages]);

  useEffect(() => {
    if (!open || sortedLanguages.length === 0) {
      return;
    }

    setActiveLanguage(sortedLanguages[0]);
  }, [open, sortedLanguages]);

  const toggleService = (serviceId) => {
    setFormData((previous) => {
      const currentIds = new Set(previous.serviceIds);
      if (currentIds.has(serviceId)) {
        currentIds.delete(serviceId);
      } else {
        currentIds.add(serviceId);
      }

      return {
        ...previous,
        serviceIds: [...currentIds],
      };
    });
  };

  const handleSubmit = () => {
    const nextErrors = {};
    const hasAllNames = supportedLanguages.every((language) => formData.name?.[language]?.trim());

    if (!hasAllNames) {
      nextErrors.name = 'serviceCategoriesManagement.validation.nameRequired';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSave({
      ...formData,
      name: buildLocalizedFields(supportedLanguages, formData.name),
      description: buildLocalizedFields(supportedLanguages, formData.description),
    });
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      title={mode === 'edit'
        ? t('serviceCategoriesManagement.dialog.editTitle')
        : t('serviceCategoriesManagement.dialog.createTitle')}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <PrimaryActionButton onClick={handleSubmit} disabled={saving}>
            {saving
              ? t('common.saving')
              : mode === 'edit'
                ? t('serviceCategoriesManagement.dialog.saveEdit')
                : t('serviceCategoriesManagement.dialog.saveCreate')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SwitchField
          label={t('serviceCategoriesManagement.fields.active')}
          checked={formData.isActive}
          onChange={(event) => setFormData((previous) => ({
            ...previous,
            isActive: event.target.checked,
          }))}
        />

        <Box
          sx={{
            border: '1px solid',
            borderColor: errors.name ? 'error.main' : 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeLanguage}
            onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: '#f8f9fa',
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: BRAND.accentText,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: BRAND.accent,
              },
            }}
          >
            {sortedLanguages.map((language) => (
              <Tab
                key={language}
                value={language}
                label={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <LanguageFlag languageCode={language} size="1rem" />
                    <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                  </Box>
                )}
              />
            ))}
          </Tabs>

          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {`${t('serviceCategoriesManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)}) *`}
              </Typography>
              <Box
                component="input"
                type="text"
                value={formData.name?.[activeLanguage] || ''}
                onChange={(event) => setFormData((previous) => ({
                  ...previous,
                  name: {
                    ...previous.name,
                    [activeLanguage]: event.target.value,
                  },
                }))}
                sx={buildTextFieldSx(Boolean(errors.name))}
              />
              {errors.name ? (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                  {t(errors.name)}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {`${t('serviceCategoriesManagement.fields.description')} (${t(`common.languages.${activeLanguage}`)})`}
              </Typography>
              <Box
                component="textarea"
                rows={3}
                value={formData.description?.[activeLanguage] || ''}
                onChange={(event) => setFormData((previous) => ({
                  ...previous,
                  description: {
                    ...previous.description,
                    [activeLanguage]: event.target.value,
                  },
                }))}
                sx={buildTextFieldSx(false, true)}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2.5,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('serviceCategoriesManagement.fields.services')}
          </Typography>
          {services.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('serviceCategoriesManagement.fields.noServicesAvailable')}
            </Typography>
          ) : (
            <FormGroup>
              {services.map((service) => (
                <FormControlLabel
                  key={service.id}
                  control={(
                    <Checkbox
                      checked={formData.serviceIds.includes(service.id)}
                      onChange={() => toggleService(service.id)}
                    />
                  )}
                  label={getLocalizedValue(service.name, i18n.language, undefined, t('serviceCategoriesManagement.card.unnamed'))}
                />
              ))}
            </FormGroup>
          )}
        </Box>
      </Box>
    </ScrollableDialog>
  );
};

export default ServiceCategoryEditorDialog;
